import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { InstitutionalNavbar } from '../institutional/InstitutionalNavbar';
import { InstitutionalFooter } from '../institutional/InstitutionalFooter';

/**
 * LegalPageLayout — Phase 99A.2 shared legal/legal-document chrome.
 *
 * Wraps long-form legal copy in the same warm-paper light direction used by
 * `/security` and `/trust`. The legal *content* is supplied by each page
 * verbatim — no clauses are introduced here.
 *
 * Props:
 *   - eyebrow: small uppercased label (e.g. "Legal", "Privacy")
 *   - titleLead: leading words of the page title (navy ink)
 *   - titleAccent: trailing word/phrase rendered in italic champagne gold
 *   - lastUpdated: factual "Last updated: …" string, supplied by the page
 *   - testId: stable data-testid for the page wrapper
 *   - relatedLinks: optional array of `{ to|href, label }` rendered as the
 *     "Related policies" panel at the bottom of the body
 *   - children: legal long-form content (already pre-styled with prose)
 */
export const LegalPageLayout = ({
  eyebrow,
  titleLead,
  titleAccent,
  lastUpdated,
  testId,
  relatedLinks,
  children,
}) => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }} data-testid={testId}>
    <InstitutionalNavbar />

    <div className="pt-20">
      {/* Light hero */}
      <section
        style={{ background: 'var(--gpc-paper)', borderBottom: '1px solid var(--gpc-line-soft)' }}
        data-testid={`${testId}-hero`}
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-8" style={{ padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem)' }}>
          {eyebrow && (
            <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>{eyebrow}</p>
          )}
          <h1
            data-testid={`${testId}-h1`}
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(2.25rem, 4.4vw, 3.4rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: 'var(--gpc-ink)',
              marginBottom: '14px',
            }}
          >
            {titleLead}{' '}
            {titleAccent && (
              <span style={{ color: 'var(--gpc-ink-soft)', fontStyle: 'italic' }}>{titleAccent}</span>
            )}
          </h1>
          {lastUpdated && (
            <p
              data-testid={`${testId}-last-updated`}
              style={{
                fontFamily: 'Space Grotesk, system-ui, sans-serif',
                fontSize: '12px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--gpc-ink-soft)',
              }}
            >
              {lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Long-form body */}
      <section className="py-16" style={{ background: 'var(--gpc-paper)' }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <article className="legal-body">
            {children}
          </article>

          {relatedLinks && relatedLinks.length > 0 && (
            <div
              className="mt-14 p-6 rounded-xl"
              style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
              data-testid={`${testId}-related`}
            >
              <p className="gpc-eyebrow" style={{ marginBottom: '10px' }}>Related policies</p>
              <ul
                className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5"
                style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px' }}
              >
                {relatedLinks.map((l, i) => (
                  <li key={i}>
                    {l.to ? (
                      <Link to={l.to} className="legal-related-link" data-testid={`legal-link-${l.testId || i}`}>
                        {l.label} →
                      </Link>
                    ) : (
                      <a href={l.href} className="legal-related-link" data-testid={`legal-link-${l.testId || i}`}>
                        {l.label} →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-12 flex flex-wrap items-center gap-3">
            <Link to="/contact" className="gpc-cta-primary" data-testid={`${testId}-cta-contact`}>
              Contact the team
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/trust" className="gpc-cta-secondary" data-testid={`${testId}-cta-trust`}>
              Trust Center
            </Link>
          </div>
        </div>
      </section>
    </div>

    <InstitutionalFooter />

    <style>{`
      .legal-body {
        font-family: Inter, system-ui, sans-serif;
        color: var(--gpc-ink-soft);
        line-height: 1.7;
      }
      .legal-body p { font-size: 15px; margin: 0 0 1rem; }
      .legal-body h2 {
        font-family: 'Space Grotesk', system-ui, sans-serif;
        font-weight: 600;
        font-size: 18px;
        color: var(--gpc-ink);
        margin-top: 2.5rem;
        margin-bottom: 0.6rem;
        letter-spacing: -0.005em;
      }
      .legal-body h3 {
        font-family: 'Space Grotesk', system-ui, sans-serif;
        font-weight: 600;
        font-size: 15px;
        color: var(--gpc-ink);
        margin-top: 1.5rem;
        margin-bottom: 0.4rem;
      }
      .legal-body strong { color: var(--gpc-ink); font-weight: 600; }
      .legal-body ul { list-style: none; padding-left: 0; margin: 0.5rem 0 1rem; }
      .legal-body ul li {
        position: relative;
        padding-left: 1.2rem;
        margin: 0.35rem 0;
        font-size: 15px;
      }
      .legal-body ul li::before {
        content: "·";
        position: absolute;
        left: 0.3rem;
        color: var(--gpc-gold);
      }
      .legal-body a {
        color: var(--gpc-ink);
        text-decoration: underline;
        text-decoration-color: rgba(140,107,46,0.45);
        text-underline-offset: 3px;
        transition: color 0.2s ease, text-decoration-color 0.2s ease;
      }
      .legal-body a:hover {
        color: var(--gpc-gold);
        text-decoration-color: var(--gpc-gold);
      }
      .legal-body .legal-divider {
        margin: 2rem 0;
        border: 0;
        border-top: 1px solid var(--gpc-line-soft);
      }
      .legal-body .legal-footnote {
        font-size: 12px;
        color: var(--gpc-ink-soft);
        margin-top: 2rem;
        padding-top: 1.25rem;
        border-top: 1px solid var(--gpc-line-soft);
      }
      .legal-related-link {
        color: var(--gpc-ink);
        text-decoration: none;
        transition: color 0.2s ease;
      }
      .legal-related-link:hover { color: var(--gpc-gold); }
    `}</style>
  </div>
);

export default LegalPageLayout;
